/**
 * /!\ This file is auto-generated.
 *
 * This is the entry point of your standalone application.
 *
 * There are multiple tags used by the editor to inject code automatically:
 *     - `wle:auto-imports:start` and `wle:auto-imports:end`: The list of import statements
 *     - `wle:auto-register:start` and `wle:auto-register:end`: The list of component to register
 *     - `wle:auto-constants:start` and `wle:auto-constants:end`: The project's constants,
 *        such as the project's name, whether it should use the physx runtime, etc...
 *     - `wle:auto-benchmark:start` and `wle:auto-benchmark:end`: Append the benchmarking code
 */

import { loadRuntime } from '@wonderlandengine/api';
import { version } from './package.json';

import {
    extra,
    ads,
    user,
    saveGame,
    analytics,
    leaderboards,
    CookieSaveGameProvider,
    AdInPlayAdProvider

} from '@wonderlandengine/uber-sdk';

import { RotbLeaderboardsProvider } from './js/classes/RotbLeaderboardsProvider.js';
import { Config } from './js/config.js';

let sdkPromise = null;

const ErrorSeverity = {
    Undefined: 0,
    Debug: 1,
    Info: 2,
    Warning: 3,
    Error: 4,
    Critical: 5,
};

window.onerror = (message, source, lineno, colno, error) => {
    let errorMessage = `Uncaught exception: ${message.toString()}`;
    if (error && error.stack) {
        errorMessage += `\nStack: ${error.stack}`;
    } else {
        errorMessage += ` at ${source}:${lineno}:${colno}`;
    }
    window.GameAnalytics?.addErrorEvent(ErrorSeverity.Error, errorMessage);
    return false;
};

function loadGameAnalytics() {
    const GameAnalytics = window.GameAnalytics ?? require('gameanalytics').GameAnalytics;
    window.GameAnalytics = GameAnalytics;

    if (ENVIRONMENT !== 'dev') {
        GameAnalytics.setEnabledInfoLog(true);
        const GA_GAME_KEY = '3307d3a61095cca6e0740914a85c1ae6';
        const GA_SECRET_KEY = 'a918f4071f6fb88ebc2f880a9ff9b834965d48e2';
        if (ENVIRONMENT === 'staging') {
            GameAnalytics.configureBuild('staging-' + TARGET + ' ' + version);
        } else {
            GameAnalytics.configureBuild(TARGET + ' ' + version);
        }
        GameAnalytics.configureAvailableResourceItemTypes(["earn", "buy", "spend"]);
        GameAnalytics.configureAvailableResourceCurrencies(["coins"]);
        GameAnalytics.initialize(GA_GAME_KEY, GA_SECRET_KEY);
    }

    GameAnalytics.addRemoteConfigsListener({
        onRemoteConfigsUpdated: () => {
            /**
             * A/B Testing will allow us to remote-control variations of
             * the game and measure change in engangement, revenue and
             * other metrics.
             */
            const all = GameAnalytics.getRemoteConfigsContentAsString();
            const configs = JSON.parse(all);
            console.log('Received remote config.');
            for (let [k, v] of Object.entries(configs)) {
                if (k in Config) {
                    try {
                        if (typeof Config[k] === 'number') {
                            v = Number.parseFloat(v);
                        } else if (typeof Config[k] === 'boolean') {
                            v = Boolean.parse(v);
                        }
                        Config[k] = v;
                    } catch (e) {
                        GameAnalytics.addErrorEvent(
                            ErrorSeverity.Error,
                            `Unable to apply config "${k}" with value "${v}":` +
                            e.toString()
                        );
                    }
                }
            }
        },
    });

}

function loadGamePlatform() {
    let lbServerURL = SERVERURL || '';
    const lb = new RotbLeaderboardsProvider({
        leaderboardId: 'leaderboardId',
        serverUrl: lbServerURL,
    });

    if (TARGET === 'crazygames') {
        sdkPromise = import('@wonderlandengine/uber-sdk').then((providers) => {
            return new Promise((resolve) => {
                //For crazygames the user should be online
                const cg = new providers.CrazyGamesProvider();
                ads.registerProvider(cg);
                analytics.registerProvider(cg);
                saveGame.registerProvider(cg);
                extra.registerProvider(cg);
                user.registerProvider(cg);
                leaderboards.registerProvider(lb);
                cg.onReady.then(() => {
                    analytics.trackLoadingStart();
                    resolve(cg);
                });
            });

        })
    } else if (TARGET === 'poki') {
        sdkPromise = import('@wonderlandengine/uber-sdk').then((providers) => {
            return new Promise((resolve) => {
                const poki = new providers.PokiProvider();
                ads.registerProvider(poki);
                analytics.registerProvider(poki);

                const cookieProvider = new CookieSaveGameProvider('riseoftheblob-save');
                saveGame.registerProvider(cookieProvider);

                leaderboards.registerProvider(lb);

                poki.onReady.then(() => {
                    analytics.trackLoadingStart();
                    resolve(cookieProvider);
                });
            });
        });
    } else if (TARGET === 'telegram') {
        sdkPromise = import('@wonderlandengine/uber-sdk').then((providers) => {
            return new Promise((resolve) => {
                const telegram = new providers.TelegramProvider();
                user.registerProvider(telegram);
                // analytics.registerProvider(telegram);

                // const adinplay = new AdInPlayAdProvider({
                //     aipID: 'WNE',
                //     domain: 'riseoftheblobs.com',
                // });
                // ads.registerProvider(adinplay);
                const cookieProvider = new CookieSaveGameProvider('riseoftheblob-save');
                saveGame.registerProvider(cookieProvider);

                leaderboards.registerProvider(lb);

                analytics.trackLoadingStart();
                resolve(cookieProvider);

            });
        });
    }
    else {
        const cg = new CookieSaveGameProvider('riseoftheblob-save');
        saveGame.registerProvider(cg);
        leaderboards.registerProvider(lb);

        const loadAdsWhenOnline = () => {
            const adinplay = new AdInPlayAdProvider({
                aipID: 'WNE',
                domain: 'riseoftheblobs.com',
            });
            ads.registerProvider(adinplay);
            // const adp = new AppLixirAdProvider({
            //     accountId: 8488,
            //     siteId: 9027,
            //     zoneId: 2050,
            //     userId: uuidv4(),
            // });
            // ads.registerProvider(adp);
            window.removeEventListener('online', loadAdsWhenOnline);
        };
        if (navigator.onLine) {
            loadAdsWhenOnline();
        } else {
            window.addEventListener('online', loadAdsWhenOnline);
        }
    }
}

if (!WL_EDITOR) {
    loadGameAnalytics();
    loadGamePlatform();
}
if (sdkPromise) {
    await sdkPromise;
}

/* wle:auto-constants:start */
const Constants = {
    ProjectName: 'Rise of the Blobs',
    RuntimeBaseName: 'WonderlandRuntime',
    WebXRRequiredFeatures: ['local',],
    WebXROptionalFeatures: ['local','hand-tracking','hit-test',],
};
const RuntimeOptions = {
    physx: false,
    loader: false,
    xrFramebufferScaleFactor: 1,
    canvas: 'canvas',
};
/* wle:auto-constants:end */

const engine = await loadRuntime(Constants.RuntimeBaseName, RuntimeOptions);
engine.foo = 1;
engine.onLoadingScreenEnd.once(() => {
    const el = document.getElementById('version');
    if (el) setTimeout(() => el.remove(), 2000);
});

/* WebXR setup. */

function requestSession(mode) {
    engine
        .requestXRSession(
            mode,
            Constants.WebXRRequiredFeatures,
            Constants.WebXROptionalFeatures
        )
        .catch((e) => console.error(e));
}

function setupButtonsXR() {
    /* Setup AR / VR buttons */
    const arButton = document.getElementById('ar-button');
    if (arButton) {
        arButton.dataset.supported = engine.arSupported;
        arButton.addEventListener('click', () => requestSession('immersive-ar'));
    }
    const vrButton = document.getElementById('vr-button');
    if (vrButton) {
        vrButton.dataset.supported = engine.vrSupported;
        vrButton.addEventListener('click', () => requestSession('immersive-vr'));
    }
}

if (document.readyState === 'loading') {
    window.addEventListener('load', setupButtonsXR);
} else {
    setupButtonsXR();
}

try {
    await engine.loadMainScene(`${Constants.ProjectName}.bin`);
    analytics.trackLoadingStop();
} catch (e) {
    console.error(e);
    analytics.trackLoadingStop();
    window.GameAnalytics.addErrorEvent(
        ErrorSeverity.Critical,
        `Failed to load ${Constants.ProjectName}.bin: ${e.message}\n${e.stack || ''}`
    );
    window.alert(`Failed to load ${Constants.ProjectName}.bin:`, e);
}

/* wle:auto-benchmark:start */
/* wle:auto-benchmark:end */
